import { useCallback, useEffect, useRef, useState } from "react";

interface StartLoadingOptions {
  durationMs?: number;
}

interface UseActionLoadingOptions {
  defaultDurationMs?: number;
}

interface UseActionLoadingReturn {
  isLoading: (actionKey: string) => boolean;
  startLoading: (actionKey: string, options?: StartLoadingOptions) => void;
  stopLoading: (actionKey: string) => void;
  withManualLoading: <T>(
    actionKey: string,
    task: () => Promise<T>,
  ) => Promise<T>;
}

const DEFAULT_LOADING_DURATION_MS = 3000;

export const useActionLoading = (
  options?: UseActionLoadingOptions,
): UseActionLoadingReturn => {
  const defaultDurationMs =
    options?.defaultDurationMs ?? DEFAULT_LOADING_DURATION_MS;
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const timerMapRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const setLoading = useCallback((actionKey: string, value: boolean) => {
    setLoadingMap((prev) => {
      if (prev[actionKey] === value) {
        return prev;
      }
      return {
        ...prev,
        [actionKey]: value,
      };
    });
  }, []);

  const clearTimer = useCallback((actionKey: string) => {
    const timer = timerMapRef.current[actionKey];
    if (!timer) {
      return;
    }
    clearTimeout(timer);
    delete timerMapRef.current[actionKey];
  }, []);

  const stopLoading = useCallback(
    (actionKey: string) => {
      clearTimer(actionKey);
      setLoading(actionKey, false);
    },
    [clearTimer, setLoading],
  );

  const startLoading = useCallback(
    (actionKey: string, options?: StartLoadingOptions) => {
      const durationMs = options?.durationMs ?? defaultDurationMs;
      clearTimer(actionKey);
      setLoading(actionKey, true);
      timerMapRef.current[actionKey] = setTimeout(() => {
        setLoading(actionKey, false);
        delete timerMapRef.current[actionKey];
      }, durationMs);
    },
    [clearTimer, defaultDurationMs, setLoading],
  );

  const withManualLoading = useCallback(
    async <T>(actionKey: string, task: () => Promise<T>): Promise<T> => {
      clearTimer(actionKey);
      setLoading(actionKey, true);
      try {
        return await task();
      } finally {
        stopLoading(actionKey);
      }
    },
    [clearTimer, setLoading, stopLoading],
  );

  useEffect(() => {
    const timers = timerMapRef.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, []);

  const isLoading = useCallback(
    (actionKey: string) => Boolean(loadingMap[actionKey]),
    [loadingMap],
  );

  return {
    isLoading,
    startLoading,
    stopLoading,
    withManualLoading,
  };
};
